import { AnswerQuestion, Block, Requirement, Quarter } from '../types/data';
import { ResponseElement } from '../types/response';

const QUARTERS: Quarter[] = ['Q1', 'Q2', 'Q3', 'Q4'];

export const convertToMegabytes = (bytes: number): number => {
  const megabytes = bytes / (1024 * 1024);
  return Math.round(megabytes);
};

export const transformTitle = (el: { [key: string]: string | number }) => {
  const { title, ...rest } = el;
  return { ...rest, name: title }
}

// Type Guards
const isBlockRequirement = (
  data: AnswerQuestion[] | Requirement[] | Block[],
): data is Block[] => {
  return (data as Block[])[0]?.groupRequirements !== undefined;
};

const isAnswerQuestion = (
  data: AnswerQuestion[] | Requirement[] | Block[],
): data is AnswerQuestion[] => {
  return (data as AnswerQuestion[])[0]?.answer !== undefined;
};

const isRequirement = (
  data: AnswerQuestion[] | Requirement[] | Block[],
): data is Requirement[] => {
  return (data as Requirement[])[0]?.answers !== undefined;
};

export const calculatesAverageValue = (
  data: AnswerQuestion[] | Requirement[] | Block[],
  department?: AnswerQuestion['department'],
  isAverage?: boolean,
  isFocal?: boolean,
): number | 'N/A' | null => {
  let answersArray: AnswerQuestion[] = [];

  if (isBlockRequirement(data)) {
    answersArray = data.flatMap((group) =>
      group.groupRequirements
        .filter((requirement) =>
          isFocal ? requirement.isFocal === isFocal : true,
        )
        .flatMap((requirement) =>
          isAverage
            ? requirement.answers
            : requirement.answers.filter(
                (answer) => answer.department === department,
              ),
        ),
    );
  }

  if (isRequirement(data)) {
    answersArray = data.flatMap((requirement) =>
      isAverage
        ? requirement.answers
        : requirement.answers.filter(
            (answer) => answer.department === department,
          ),
    );
  }

  if (isAnswerQuestion(data)) {
    answersArray = data;
  }

  const validAnswers = answersArray.filter((answer) => answer.answer !== 'N/A');

  if (!answersArray.length) return null;

  if (validAnswers.length === 0) {
    return 'N/A';
  }

  const { sum, count } = answersArray.reduce(
    (acc, question) => {
      if (question.answer === 'Да') {
        acc.sum += 100;
        acc.count += 1;
      } else if (question.answer === 'Нет') {
        acc.sum += 0;
        acc.count += 1;
      }

      return acc;
    },
    { sum: 0, count: 0 },
  );

  return count > 0 ? Math.round(sum / count) : 0;
};

export const isNotStartedQuestionnaire = (data: Block[]) => {
  return data.every(block =>
    block.groupRequirements.every(requirement =>
      requirement.answers.every(answer => answer.answerId === null)
    )
  );
}

const quarterToIndex = (quarter: Quarter): number => {
  return { Q1: 0, Q2: 1, Q3: 2, Q4: 3 }[quarter];
};

export const getQuarterNumber = (year: number, quarter: Quarter): number => {
  return year * 4 + quarterToIndex(quarter);
};

export const filterByRange = (
  data: ResponseElement[],
  quarter: Quarter,
  year: number
): ResponseElement[] => {
  const target = getQuarterNumber(year, quarter);

  return data.filter(({ showElement }) => {
    if (!showElement || showElement.length === 0) return true;

    return showElement.some(({ startYear, startQuarter, finishYear, finishQuarter }) => {
      const start = getQuarterNumber(startYear, startQuarter);
      const finish = getQuarterNumber(finishYear, finishQuarter);

      return target >= start && target <= finish;
    });
  });
};

export function getCurrentQuarterAndYear(data) {
  const current = +new Date();

  for (const item of data) {
    const start = new Date(item.startDate).setHours(0, 0, 0);
    const end = new Date(item.finishDate).setHours(23, 59, 59);

    if (current >= start && current <= end) {
      return {
        quarter: item.quarter,
        year: item.year,
        finishDate: item.finishDate
      };
    }
  }

  return null;
}

export const getIdQuarter = (quarter: Quarter) => {
  const index = QUARTERS.indexOf(quarter);
  return index + 1;
}

export const getLabelQuarter = (arr: number[]): Quarter[] => {
  return arr.map((index) => QUARTERS[index - 1]);
}

export const findQuestionnaire = (data, year, quarter, factory, direction) => {
  const result = data.find((item) =>
    item.quarter === quarter &&
    item.year === year &&
    item.factory?.title === factory &&
    item.direction?.Title === direction
  );

  return result ?? {
    completionDate: "В процессе",
    nameCompleted: "В процессе"
  };
}

export const getDateAsString = (str: string | 'В процессе' | null): string => {
  if (str === 'В процессе') return str;

  if (str === null) return 'В процессе';

  const date = new Date(str);

  const options = {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  };

  return date.toLocaleString('ru-RU', options);
}

export const isEmptyString = (str: string) => {
  return typeof str !== "string" || str.trim() === "";
}
